import React, { useEffect, useState } from 'react';
import './App.css';
import './css/buttons.css';
import { useDispatch } from 'react-redux';
import { getAppInitializedAction } from './redux/actions/context';
import RoutingProvider from './providers/RoutingProvider';
import { AgoroHeader } from './components/agoro-header/AgoroHeader.component';
import { useLocation } from 'react-router-dom';
import { Box, Grid } from 'grommet';
import { useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { hotjar } from 'react-hotjar';
import { I18nextProvider } from 'react-i18next';
import i18n from './i18n/i18n';
import { User } from './redux/reducers/user-reducer';
import useCognito from './components/Cognito/UseCognito';
import { setLambda } from './service/common/env';
import { useAppSelector } from './redux';

const App = () => {
  const { pathname } = useLocation();
  const [isBaseLDInitialized, setIsBaseLDInitialized] = useState(false);
  const dispatch = useDispatch();
  const baseLDClient = useLDClient();
  hotjar.initialize(process.env.REACT_APP_HOTJAR_ID ? Number(process.env.REACT_APP_HOTJAR_ID) : 3288859, 6);
  const { getTokensOrLogin, refreshSessionSilently } = useCognito();

  useEffect(() => {
    getTokensOrLogin().then(({ token, user }) => {
      dispatch(getAppInitializedAction(user, token, refreshSessionSilently));
    });
  }, []);

  const isInitialized = useAppSelector((state) => state.context.isInitialized);
  const userData: User = useAppSelector((state) => state.user.userData);
  const userLanguage = userData?.language;

  const removeLoaderContainer = () => {
    document.getElementById('loader-container')?.remove();
  };

  if (userLanguage) {
    if (!i18n.language || i18n.language !== userLanguage) {
      i18n.changeLanguage(userLanguage).then(() => {
        setTimeout(() => {
          removeLoaderContainer();
        }, 700);
      });
    } else {
      removeLoaderContainer();
    }
  }

  useEffect(() => {
    if (baseLDClient && userData) {
      const ldUserData = {
        kind: 'user',
        key: userData.id,
        firstName: userData.firstName,
        lastName: userData.lastName,
        name: userData.fullName,
        email: userData.email,
        country: userData.country,
        roles: userData.roles,
        partnerTier: userData.partnerTier,
      };

      baseLDClient.identify(ldUserData).then(() => {
        setIsBaseLDInitialized(true);
      });
    }
    if (hotjar.initialized() && userData) {
      hotjar.identify(userData.id, { userProperty: { roles: userData.roles } });
    }
  }, [baseLDClient, userData]);

  useEffect(() => {
    if (hotjar.initialized()) {
      hotjar.stateChange(pathname);
    }
  }, [pathname]);

  const { useLambdaBackend } = useFlags();

  useEffect(() => {
    setLambda(useLambdaBackend);
  }, [useLambdaBackend]);

  return isBaseLDInitialized && isInitialized && userData ? (
    <Grid
      height={{ min: '100vh' }}
      className='App'
      fill
      rows={['auto', 'flex']}
      columns={['auto', 'flex']}
      areas={[
        { name: 'header', start: [0, 0], end: [1, 0] },
        { name: 'main', start: [0, 1], end: [1, 1] },
      ]}>
      <Box gridArea='header' align='center' justify='center'>
        <AgoroHeader />
      </Box>
      <Box gridArea='main'>
        <I18nextProvider i18n={i18n}>
          <RoutingProvider />
        </I18nextProvider>
      </Box>
    </Grid>
  ) : null;
};

export default App;
